import { Flex, Tag, Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { normalizeLinksInHtml } from 'utils/htmlHelpers';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';

import { ActionsDropdown } from './dropdown';
import styles from './styles.module.scss';
import { TemporaryNote } from './temporary-note';

import type { IKeyNoteData } from 'infrastructure/interfaces';

interface IProps {
  keynote: IKeyNoteData;
  onEdit?: () => void;
  onDelete?: () => void;
  onShowHistory?: () => void;
  showActions?: boolean;
  module?: ModuleType;
  isInsideModal?: boolean;
}

export const KeyNote = ({
  keynote,
  onDelete,
  onShowHistory,
  onEdit,
  showActions,
  module,
  isInsideModal,
}: IProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const [shouldTruncate, setShouldTruncate] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;
      element.classList.add(styles.truncated);
      const needsTruncation = element.scrollHeight > element.clientHeight + 1;
      element.classList.remove(styles.truncated);
      setShouldTruncate(needsTruncation);
    }
  }, [keynote?.note]);

  return (
    <Flex className={styles['key-note-content-wrapper']}>
      {keynote.expiresAt && (
        <Flex className={styles['key-note-header']}>
          <TemporaryNote
            note={keynote}
            module={module}
            isInsideModal={isInsideModal}
          />
          {showActions && (
            <ActionsDropdown
              note={keynote}
              onEdit={() => {
                onEdit?.();
              }}
              onShowHistory={() => {
                onShowHistory?.();
              }}
              onDelete={() => {
                onDelete?.();
              }}
            />
          )}
        </Flex>
      )}

      {!keynote.expiresAt && (
        <Flex className={styles['key-note-header']}>
          {module && (
            <Tag
              color={module === ModuleType.RPM ? 'orange' : 'blue'}
              className={styles['module-tag']}
            >
              {module.toUpperCase()}
            </Tag>
          )}
          {showActions && (
            <ActionsDropdown
              note={keynote}
              onEdit={() => {
                onEdit?.();
              }}
              onShowHistory={() => {
                onShowHistory?.();
              }}
              onDelete={() => {
                onDelete?.();
              }}
            />
          )}
        </Flex>
      )}

      <>
        <div
          ref={contentRef}
          data-cy="patient-key-note-content"
          className={`${styles['raw-html']} ${
            !expanded && shouldTruncate ? styles.truncated : ''
          }`}
          dangerouslySetInnerHTML={{
            __html: normalizeLinksInHtml(keynote?.note || ''),
          }}
        />
        {shouldTruncate && (
          <Button
            type="link"
            onClick={() => setExpanded(!expanded)}
            className={styles['expand-button']}
          >
            <Flex gap={8} className={styles['expand-button']}>
              {expanded ? (
                <>
                  {t('labels.showLess', 'Show less')} <UpOutlined />
                </>
              ) : (
                <>
                  {t('labels.showMore', 'Show more')} <DownOutlined />
                </>
              )}
            </Flex>
          </Button>
        )}
      </>

      <Flex className={styles['key-note-footer']}>
        <Flex className={styles['actions-by-wrapper']}>
          <div>
            {keynote?.createdAt && keynote?.createdBy && (
              <Flex data-cy="patient-key-note-updated-by">
                {t('labels.electronicallySignedBy')}:{' '}
                <Flex className={styles['key-note-author']}>
                  {keynote.createdBy}
                </Flex>
                <Flex className={styles['separating-point']}>•</Flex>
                <Flex className={styles['formatted-date']}>
                  {DateFormatter({
                    date: keynote.createdAt,
                    format: DateFormats['MM/dd/yyyy, h:mm a'],
                  })}
                </Flex>
              </Flex>
            )}
            {keynote?.updatedBy && keynote?.updatedAt && (
              <Flex data-cy="patient-key-note-updated-by">
                {t('labels.lastModifiedBy')}:{' '}
                <Flex className={styles['key-note-author']}>
                  {keynote.updatedBy}
                </Flex>
                <Flex className={styles['separating-point']}>•</Flex>
                <Flex className={styles['formatted-date']}>
                  {DateFormatter({
                    date: keynote.updatedAt,
                    format: DateFormats['MM/dd/yyyy, h:mm a'],
                  })}
                </Flex>
              </Flex>
            )}
          </div>
        </Flex>
      </Flex>
    </Flex>
  );
};
